import { Form, message } from "antd";
import axios from "axios";
import { useEffect, useState } from "react";

export const useRatePlan = () => {
  const [ratePlans, setRatePlans] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [cancellationPolicies, setCancellationPolicies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingRatePlan, setEditingRatePlan] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchRatePlans();
    fetchRoomsAndPolicies();
  }, []);

  const fetchRatePlans = async () => {
    setLoading(true);
    try {
      const response = await axios.get("/api/rateplans/getallrateplans");
      setRatePlans(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      message.error("Failed to fetch rate plans.");
      setRatePlans([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchRoomsAndPolicies = async () => {
    try {
      const [roomsResponse, policiesResponse] = await Promise.all([
        axios.get("/api/rooms/getallrooms"),
        axios.get("/api/policies/getcancellationpolicies"),
      ]);
      setRooms(Array.isArray(roomsResponse.data) ? roomsResponse.data : []);
      if (Array.isArray(policiesResponse.data)) {
        setCancellationPolicies(policiesResponse.data);
      } else {
        console.error("Unexpected response format for cancellation policies");
      }
    } catch (error) {
      message.error("Failed to fetch rooms or cancellation policies.");
    }
  };

  const handleCreate = () => {
    setEditingRatePlan(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (ratePlan) => {
    const roomIds = ratePlan.room
      .map((roomId) => {
        const room = rooms.find((r) => r._id === roomId);
        return room ? room._id : "";
      })
      .filter(Boolean);
    setEditingRatePlan(ratePlan);
    const prefilledData = {
      ...ratePlan,
      cancellationPolicy: JSON.stringify(ratePlan.cancellationPolicy),
      extraBenefits: ratePlan.extraBenefits || [],
      room: roomIds,
      closeout: ratePlan.closeout || false,
      discount:
        typeof ratePlan.discount !== "undefined" ? ratePlan.discount : 0,
    };
    form.setFieldsValue(prefilledData);
    setIsModalVisible(true);
  };

  const handleDelete = async (ratePlanId) => {
    try {
      await axios.delete(`/api/rateplans/delete/${ratePlanId}`);
      message.success("Rate plan deleted successfully.");
      fetchRatePlans();
    } catch (error) {
      message.error("Failed to delete rate plan.");
    }
  };

  const handleFormSubmit = async () => {
    try {
      const values = await form.validateFields();
      const cancellationPolicy = JSON.parse(values.cancellationPolicy);

      const ratePlanData = {
        rateplanname: values.rateplanname,
        room: values.room,
        rateplanpricing: values.room.map((roomId) => {
          const existingPricing =
            editingRatePlan?.rateplanpricing.find(
              (rp) => rp.roomId === roomId
            ) || {};
          return {
            roomId,
            manualRate: existingPricing.manualRate || {},
            deriveFromRentperdayByDate:
              existingPricing.deriveFromRentperdayByDate || false,
          };
        }),
        cancellationPolicy,
        mealPlan: values.mealPlan,
        extraBenefits: values.extraBenefits || [],
        ratePlanDescription: values.ratePlanDescription || "",
        ratePlanSetup: values.ratePlanSetup,
        closeout: values.closeout || false,
        discount: typeof values.discount !== "undefined" ? values.discount : 0,
      };

      if (editingRatePlan) {
        await axios.put(
          `/api/rateplans/update/${editingRatePlan._id}`,
          ratePlanData
        );
        message.success("Rate plan updated successfully.");
      } else {
        await axios.post("/api/rateplans/create", ratePlanData);
        message.success("Rate plan created successfully.");
      }

      setIsModalVisible(false);
      form.resetFields();
      fetchRatePlans();
    } catch (error) {
      message.error("Failed to save rate plan.");
    }
  };

  return {
    ratePlans,
    rooms,
    cancellationPolicies,
    loading,
    isModalVisible,
    editingRatePlan,
    handleCreate,
    handleEdit,
    handleDelete,
    handleFormSubmit,
    setIsModalVisible,
    form,
  };
};
